'use client';

import type { LinkProps } from 'next/link';
import Link from '@/components/auth/LoginLink';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { LiquidGlass } from './liquid-glass/LiquidGlass';
import controls from '@/components/public-site/PublicSiteControls.module.css';
import styles from './StudioAction.module.css';

type SharedProps = {
  children: ReactNode;
  variant?: 'primary' | 'secondary';
  size?: 'default' | 'compact';
  className?: string;
};
type StudioActionProps = SharedProps & (
  | ({ href: LinkProps['href'] } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof SharedProps | 'href'> & Pick<LinkProps, 'prefetch' | 'replace' | 'scroll'>)
  | ({ href?: never } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof SharedProps>)
);

/** The homepage's actual lens renderer and control material, for links and actions. */
export function StudioAction({ children, variant = 'primary', size = 'default', className, ...props }: StudioActionProps) {
  const material = cn(controls.button, controls[variant], size === 'compact' && controls.compact);
  if (props.href !== undefined) {
    const { href, ...linkProps } = props;
    return <LiquidGlass className={cn(material, styles.linkShell, size === 'compact' && styles.compact, className)}>
      <Link {...linkProps as AnchorHTMLAttributes<HTMLAnchorElement>} href={href} className={styles.link}>{children}</Link>
    </LiquidGlass>;
  }
  return <LiquidGlass as="button" type="button" {...props as ButtonHTMLAttributes<HTMLButtonElement>} className={cn(material, className)}>{children}</LiquidGlass>;
}
